import * as React from "react";
import {
  Avatar,
  Button,
  Typography,
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import LogoSinsoft from "../../../Assets/logo-sinsoft.png";
import { useNavigate, useParams } from "react-router-dom";
import { useUsuario } from "../../../Hooks/UsuarioHook";
import { useUsuarioStorage } from "../../../Storage/UsuariosStorage";
import { app_base_url } from "../../../Utils/Urls";
import { useSistemaStorage } from "../../../Storage/SistemaStorage";
import { FiltrosUsuarios } from "./Filtros";
import { Footer } from "./Footer";

const formatTelefone = (value?: string) => {
  if (!value) return "-";
  const digits = value.replace(/\D/g, "");
  if (digits.length === 11) {
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }
  if (digits.length === 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }
  return value;
};

export function Usuarios() {
  const navigate = useNavigate();
  const { usuario, token } = useUsuario();
  const {
    usuarios,
    totalUsuarios,
    getUsuariosPaginado,
    getUsuariosTodosFiltrados,
    getUsuariosPorSistema,
  } = useUsuarioStorage();
  const { getSistemaPorId } = useSistemaStorage();
  const { sistema_id } = useParams();
  const emModoSistema = Boolean(sistema_id);

  // Boolean(): `administrador` vem do MySQL como tinyint (0/1) — sem isso o
  // `0 && <Box/>` renderizaria o literal 0 na tela do não-administrador.
  // Mesma condição da rota /criar-usuario (app.routes.tsx): botão e rota andam
  // juntos, senão aparece botão levando para uma rota que não existe.
  const podeCriarUsuario =
    Boolean(usuario?.usuario?.administrador) && !emModoSistema;

  // Filtros
  const [searchTerm, setSearchTerm] = React.useState("");
  const [status, setStatus] = React.useState("ativos");
  const [servidor, setServidor] = React.useState("todos");
  const [sexo, setSexo] = React.useState("todos");
  const [zona_rural, setZonaRural] = React.useState("todas");
  const [idadeMin, setIdadeMin] = React.useState("");
  const [idadeMax, setIdadeMax] = React.useState("");
  const [profissao, setProfissao] = React.useState("");
  const [bairro, setBairro] = React.useState("");
  const [cadastroInicio, setCadastroInicio] = React.useState("");
  const [cadastroFim, setCadastroFim] = React.useState("");

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const isAdmin = usuario.usuario.administrador;

  const filtros = React.useMemo(
    () => ({
      search: searchTerm,
      status,
      servidor,
      sexo,
      zonaRural: zona_rural,
      idadeMin,
      idadeMax,
      profissao,
      bairro,
      cadastroInicio,
      cadastroFim,
    }),
    [
      searchTerm,
      status,
      servidor,
      sexo,
      zona_rural,
      idadeMin,
      idadeMax,
      profissao,
      bairro,
      cadastroInicio,
      cadastroFim,
    ]
  );

  // Volta para a primeira página quando os filtros mudam
  React.useEffect(() => {
    setPage(0);
  }, [filtros]);

  // MODO SISTEMA: carrega os usuários do sistema (client-side)
  React.useEffect(() => {
    if (!emModoSistema) return;
    if (usuario.usuario.id && token.length > 0) {
      getUsuariosPorSistema(Number(sistema_id));
      getSistemaPorId(Number(sistema_id));
    }
  }, [emModoSistema, usuario.usuario.id, token, sistema_id]);

  // MODO PRINCIPAL: busca paginada NO SERVIDOR (com debounce na digitação)
  React.useEffect(() => {
    if (emModoSistema) return;
    if (!usuario.usuario.id || token.length === 0) return;

    const t = setTimeout(() => {
      getUsuariosPaginado({ ...filtros, page, pageSize: rowsPerPage });
    }, 350);

    return () => clearTimeout(t);
  }, [emModoSistema, usuario.usuario.id, token, filtros, page, rowsPerPage]);

  // Em modo sistema pagina em memória; em modo principal já vem a página do servidor
  const usuariosExibidos = emModoSistema
    ? usuarios.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : usuarios;
  const totalRegistros = emModoSistema ? usuarios.length : totalUsuarios;

  // Relatório: no modo principal busca TODOS os filtrados do servidor
  const obterRegistrosRelatorio = async () => {
    if (emModoSistema) return usuarios;
    return await getUsuariosTodosFiltrados(filtros);
  };

  return (
    <Box>
      <FiltrosUsuarios
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        status={status}
        setStatus={setStatus}
        servidor={servidor}
        setServidor={setServidor}
        sexo={sexo}
        setSexo={setSexo}
        zonaRural={zona_rural}
        setZonaRural={setZonaRural}
        idadeMin={idadeMin}
        setIdadeMin={setIdadeMin}
        idadeMax={idadeMax}
        setIdadeMax={setIdadeMax}
        profissao={profissao}
        setProfissao={setProfissao}
        bairro={bairro}
        setBairro={setBairro}
        cadastroInicio={cadastroInicio}
        setCadastroInicio={setCadastroInicio}
        cadastroFim={cadastroFim}
        setCadastroFim={setCadastroFim}
        resetFiltros={() => {
          setSearchTerm("");
          setStatus("ativos");
          setServidor("todos");
          setSexo("todos");
          setZonaRural("todas");
          setIdadeMin("");
          setIdadeMax("");
          setProfissao("");
          setBairro("");
          setCadastroInicio("");
          setCadastroFim("");
        }}
      />

      <Paper
        elevation={0}
        sx={{
          mt: 2,
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        <TableContainer sx={{ maxHeight: "calc(100vh - 340px)" }}>
          <Table stickyHeader size="medium">
            <TableHead>
              <TableRow>
                {["Foto", "Nome", "Profissão", "Telefone", "E-mail"].map((h, i) => (
                  <TableCell
                    key={h}
                    align={i === 0 ? "center" : "left"}
                    sx={{
                      bgcolor: "primary.main",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 11,
                      letterSpacing: 0.6,
                      textTransform: "uppercase",
                      borderBottom: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
                {isAdmin && (
                  <TableCell
                    align="center"
                    sx={{
                      bgcolor: "primary.main",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 11,
                      letterSpacing: 0.6,
                      textTransform: "uppercase",
                      borderBottom: "none",
                    }}
                  >
                    Ações
                  </TableCell>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {usuariosExibidos.map((item, idx) => (
                <TableRow
                  key={item.usuario.id}
                  hover
                  sx={{
                    bgcolor: idx % 2 === 0 ? "background.paper" : "action.hover",
                    "&:hover": { bgcolor: "rgba(79,142,247,0.08)" },
                    "& td": { borderColor: "divider" },
                  }}
                >
                  <TableCell align="center" sx={{ py: 1 }}>
                    <Avatar
                      src={item.usuario.foto}
                      alt={item.usuario.nome}
                      sx={{ width: 44, height: 44, mx: "auto" }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                      {item.usuario.nome}
                    </Typography>
                    {item.usuario.cpf && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {item.usuario.cpf}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 1, maxWidth: 220 }}>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {item.usuario.descricao || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" noWrap>
                      {formatTelefone(item.usuario.telefone)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1, maxWidth: 240 }}>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {item.usuario.email || "—"}
                    </Typography>
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="center" sx={{ py: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() =>
                          navigate(`${app_base_url}/perfil/${item.usuario.id}`)
                        }
                        sx={{ textTransform: "none", borderRadius: 2 }}
                      >
                        Editar
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}

              {usuariosExibidos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 6 : 5} sx={{ border: "none" }}>
                    <Box
                      sx={{
                        py: 8,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                        color: "text.secondary",
                      }}
                    >
                      <PeopleOutlineIcon sx={{ fontSize: 48, opacity: 0.5 }} />
                      <Typography>Nenhum usuário encontrado.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Rodapé da tabela: gerar relatório + desenvolvido por + paginação */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            borderTop: 1,
            borderColor: "divider",
            px: 2,
            py: 1,
          }}
        >
          <Footer
            total={totalRegistros}
            obterRegistros={obterRegistrosRelatorio}
            status={status}
            servidor={servidor}
            sexo={sexo}
            zonaRural={zona_rural}
            idadeMin={idadeMin}
            idadeMax={idadeMax}
            profissao={profissao}
            bairro={bairro}
            cadastroInicio={cadastroInicio}
            cadastroFim={cadastroFim}
            searchTerm={searchTerm}
            /* undefined para quem não pode criar: o botão nem chega a existir.
               Cadastro nasce na entidade de quem está logado — a tela não
               pergunta a entidade e o Perfil usa o entidade_id do token. */
            onNovoUsuario={
              podeCriarUsuario
                ? () => navigate(`${app_base_url}/criar-usuario`)
                : undefined
            }
          />

          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Desenvolvido por
            </Typography>
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: 1,
                px: 0.75,
                py: 0.3,
                display: "flex",
                border: "1px solid rgba(15,23,42,0.06)",
              }}
            >
              <Box
                component="img"
                src={LogoSinsoft}
                alt="Sinsoft Informática"
                sx={{ height: 15, width: "auto", display: "block" }}
              />
            </Box>
          </Box>

          <TablePagination
            component="div"
            count={totalRegistros}
            page={page}
            onPageChange={(_, novaPagina) => setPage(novaPagina)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </Box>
      </Paper>
    </Box>
  );
}
